/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BackupRestoreApiService, ServicesApiService, SystemApiService } from '../../../../shared/http';
import { BaseComponent } from '../../../../../../core/components';
import { ClusterService } from '../../../../shared/services/utilities';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import {
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    UserService,
    UtilsService
} from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-restore-backup',
    templateUrl: './restore-backup.component.html',
    styleUrls: ['./restore-backup.component.scss']
})
export class RestoreBackupComponent extends BaseComponent {
    /**
     * Form group name.
     */
    public restoreForm: FormGroup;

    /**
     * To store the restore data.
     */
    public restoreData = {
        file: '',
        viewDateTime: '',
        dateTime: '',
        version: '',
        nodes: [],
        sections: []
    };

    /**
     * Store the backup local node state.
     */
    public isNodeInCluster: boolean = false;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdownContent: any[] = [];

    /**
     * A selection has been made when the dropdown is shown.
     */
    public restoreType: any = null;

    /**
     * The mode for the type of selection in the table.
     */
    public tableSelectMode: 'multiRow' | 'none' = 'none';

    /**
     * Store filtered section table rows.
     */
    public sectionTableRows: any[] = [];

    /**
     * Store the selected section rows.
     */
    public selectedSectionRows: any[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    constructor(
        private backupApiService: BackupRestoreApiService,
        private clusterService: ClusterService,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private servicesAPI: ServicesApiService,
        private systemApiService: SystemApiService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.restoreData = this.utilsService.getObjectValue(this.router.getCurrentNavigation(), 'extras.state');
        if (!this.restoreData) {
            this.router.navigate(['../'], { relativeTo: this.route }).then().catch();
        }

        this.setUpFormGroup();
        this.setupFormSubscribers();
        this.setDropdownContent();
        this.setNodeInCluster();
        this.populateSectionTableRows();
        this.registerButtons();
    }

    /**
     * Selected row from cluster table.
     * @param $event
     */
    public setRestoreType($event: any): void {
        this.restoreType = $event;
        if ($event === 'partRestore') {
            this.selectedSectionRows = [];
            this.restoreForm.get('sections').setValue([]);
            this.tableSelectMode = 'multiRow';
        } else {
            this.selectedSectionRows = this.sectionTableRows;
            this.restoreForm.get('sections').setValue(this.selectedSectionRows.map(item => item.name));
            this.tableSelectMode = 'none';
            const nodeId = this.restoreForm.get('nodeId');
            nodeId.setValue($event);
            nodeId.markAsDirty();
        }
    }

    /**
     * Selected row from sections table.
     * @param $event
     */
    public setSelectedSections($event: any): void {
        this.selectedSectionRows = $event;
        const names = $event.map((item: { name: any; }) => item.name);
        const formSections = this.restoreForm.get('sections');
        formSections.setValue(names);
        if (names.length) {
            formSections.markAsDirty();
        } else {
            formSections.markAsPristine();
        }
    }

    /**
     * Send the restore call.
     */
    public restore(): Observable<any> {
        return new Observable((observer) => {
            const formValues = this.restoreForm.value;
            if (!formValues.nodeId) {
                delete formValues.nodeId;
            } else {
                delete formValues.sections;
            }

            this.backupApiService.restore(formValues)
                .pipe(
                    concatMap(() => this.servicesAPI.applyAll()),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (): void => {
                        const formValues = this.restoreForm.value;
                        if (this.restoreType !== 'partRestore' ||
                            (formValues?.sections.includes('rbac'))) {
                            this.confirmRestart();
                        } else {
                            this.router.navigate(['../'], { relativeTo: this.route }).then().catch();
                        }
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.notifyService.error(error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Confirm restart modal.
     */
    public confirmRestart(): void {
        const confirmationObject: any = {
            title: 'restart',
            message: 'system_control_confirm_restart',
            callBack: this.restart.bind(this),
            options: {
                isOverlayClickable: false,
                showCloseButton: false,
                hideCancelButton: true,
                translateText: true
            }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Restart appliance after restore.
     * @private
     */
    private restart(): Observable<any> {
        return new Observable((observer) => {
            this.systemApiService.restart()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.userService.logout(false);
                        this.router.navigateByUrl('/restart').then();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'RestoreBackupComponent: restart',
                            'Error submitting reboot request',
                            error
                        );
                        this.modalService.closeAll();
                        this.notifyService.error(error.message);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up form group.
     * @private
     */
    private setUpFormGroup() {
        this.restoreForm = this.formBuilder.group({
            file: [this.restoreData.file],
            nodeId: [],
            sections: [[]],
            fipActive: [false]
        });

        this.initialFormData = this.restoreForm.value;
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.restoreForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.utilsService.debounce('form', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, values)) {
                            this.restoreForm.markAsPristine();
                        }
                    });
                }
            });

        this.restoreForm.get('fipActive').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: fipActive => {
                    if (fipActive) {
                        const sections: AbstractControl = this.restoreForm.get('sections');
                        if (!sections.value.includes('fip')) {
                            sections.value.push('fip');
                            this.selectedSectionRows = this.sectionTableRows.filter(item => sections.value.includes(item.name));
                        }
                    }
                }
            });
    }

    /**
     * Set dropdown content.
     * @private
     */
    private setDropdownContent(): void {
        this.dropdownContent.push(
            <DropdownModel>{ key: 'partRestore', value: 'Part Restore' }
        );

        for (const node of this.restoreData.nodes) {
            this.dropdownContent.push(
                <DropdownModel>{ key: node.id, value: node.hostname }
            );
        }
    }

    /**
     * Set backupForLocalNode variable.
     * @private
     */
    private setNodeInCluster(): void {
        if (this.clusterService.isNodeInCluster()) {
            this.isNodeInCluster = true;
            this.restoreType = 'partRestore';
            this.tableSelectMode = 'multiRow';
        }
    }

    /**
     * Populate section table rows, filter out non partRestore items.
     * @private
     */
    private populateSectionTableRows(): void {
        this.sectionTableRows = [];

        for (const section of this.restoreData.sections) {
            if (section.partRestore) {
                this.sectionTableRows.push({
                    name: section.name,
                    nameFull: section.nameFull,
                    version: section.version || 'n/a'
                });
            }
        }

        if (!this.isNodeInCluster) {
            this.selectedSectionRows = this.sectionTableRows;
            this.restoreForm.get('sections').setValue(this.selectedSectionRows.map(item => item.name));
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'backupRestore-confirm-restore': [{ path: '/backup/restore', methods: ['POST'] }],
            'sysControl-restart': [{ path: '/system/reboot', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['prometheus-set-exporters']) {
            this.restoreForm.disable();
        }
    }
}
